import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import { Badge, Button, Dialog } from '@risksmart-app/atomic-ui';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import Table from '@risksmart-app/components/src/table';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRetriggerDelivery } from '@/hooks/mutations/webhook';
import { useListWebhookDeliveries } from '@/hooks/queries';
import { toLocalDateTime } from '@/utils/dateUtils';
import { EMPTY_CELL } from '@/utils/EmptyCell';

import WebhookDeliveryStatusBadge from '../WebhookDeliveryStatusBadge';
import RetriggerDeliveryModal from './RetriggerDeliveryModal';

type GetWebhookDeliveriesData = NonNullable<
  ReturnType<typeof useListWebhookDeliveries>['data']
>;

type DeliveryRow =
  GetWebhookDeliveriesData['organisation_webhook_delivery'][number];

interface Props {
  webhookId: string;
  webhookTitle: string;
  onDismiss: () => void;
}

const WebhookDeliveryLogsModal: FC<Props> = ({
  webhookId,
  webhookTitle,
  onDismiss,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'webhooks.deliveryLogs',
  });

  const { data, loading, error } = useListWebhookDeliveries({
    queryArgs: { webhookId },
  });

  const deliveries = data?.organisation_webhook_delivery ?? [];

  // Payload and response body both show in a nested dialog; a popover would
  // portal into Base UI's inert layer and not be interactive. `null` = closed.
  const [viewer, setViewer] = useState<{
    title: string;
    content: string;
  } | null>(null);

  const { addNotification } = useNotifications();
  const { retriggerDelivery } = useRetriggerDelivery();

  const [selectedDeliveries, setSelectedDeliveries] = useState<DeliveryRow[]>(
    []
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRetriggering, setIsRetriggering] = useState(false);

  const selectedDelivery =
    selectedDeliveries.length === 1 ? selectedDeliveries[0] : null;
  // Re-triggering a `test` delivery as a real `manual` send is meaningless.
  const canRetrigger =
    selectedDelivery !== null && selectedDelivery.DeliveryType !== 'test';

  const handleRetrigger = async () => {
    if (!selectedDelivery) {
      return;
    }
    setIsRetriggering(true);
    try {
      const { status } = await retriggerDelivery(selectedDelivery.Id);
      switch (status) {
        case 'queued':
          addNotification({ type: 'success', content: t('retrigger.success') });
          break;
        case 'already_queued':
          addNotification({
            type: 'error',
            content: t('retrigger.alreadyQueued'),
          });
          break;
        case 'not_replayable':
          addNotification({
            type: 'error',
            content: t('retrigger.notReplayable'),
          });
          break;
        default:
          addNotification({ type: 'error', content: t('retrigger.error') });
      }
    } catch {
      addNotification({ type: 'error', content: t('retrigger.error') });
    } finally {
      setIsRetriggering(false);
      setIsConfirmOpen(false);
      // Keep the row selected; no refetch — the manual row appears ~1 min later.
    }
  };

  const deliveryTypeLabel = (deliveryType: string): string => {
    switch (deliveryType) {
      case 'initial':
        return t('deliveryType.initial');
      case 'retry':
        return t('deliveryType.retry');
      case 'manual':
        return t('deliveryType.manual');
      default:
        return deliveryType;
    }
  };

  // Response bodies are stored as a string that is usually JSON; pretty-print
  // when parseable, otherwise show the raw text.
  const formatResponseBody = (raw: string): string => {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  const columnDefinitions: TableProps.ColumnDefinition<DeliveryRow>[] = [
    {
      id: 'DispatchedAt',
      header: t('columns.dispatchedAt'),
      cell: (item) => toLocalDateTime(item.DispatchedAt) || EMPTY_CELL,
    },
    {
      id: 'EventType',
      header: t('columns.eventType'),
      cell: (item) => item.EventType,
    },
    {
      id: 'Status',
      header: t('columns.status'),
      cell: (item) => <WebhookDeliveryStatusBadge status={item.Status} />,
    },
    {
      id: 'DeliveryType',
      header: t('columns.deliveryType'),
      cell: (item) => (
        <Badge variant={'secondary'} className={'w-fit min-w-20'}>
          {deliveryTypeLabel(item.DeliveryType)}
        </Badge>
      ),
    },
    {
      id: 'HttpStatusCode',
      header: t('columns.httpStatusCode'),
      cell: (item) => item.HttpStatusCode ?? EMPTY_CELL,
    },
    {
      id: 'ResponseBodyPreview',
      header: t('columns.responseBodyPreview'),
      maxWidth: 320,
      cell: (item) => {
        const body = item.ResponseBodyPreview;
        if (!body) {
          return EMPTY_CELL;
        }

        return (
          <Button
            variant={'neutral'}
            style={'ghost'}
            size={'sm'}
            aria-label={t('viewResponseAria')}
            onClick={() =>
              setViewer({
                title: t('responseHeading'),
                content: formatResponseBody(body),
              })
            }
          >
            {t('viewPayload')}
          </Button>
        );
      },
    },
    {
      id: 'ErrorMessage',
      header: t('columns.errorMessage'),
      maxWidth: 240,
      cell: (item) => item.ErrorMessage ?? EMPTY_CELL,
    },
    {
      id: 'Payload',
      header: t('columns.payload'),
      cell: (item) => (
        <Button
          variant={'neutral'}
          style={'ghost'}
          size={'sm'}
          aria-label={t('viewPayloadAria')}
          onClick={() =>
            setViewer({
              title: t('payloadHeading'),
              content: JSON.stringify(item.Payload, null, 2),
            })
          }
        >
          {t('viewPayload')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onDismiss();
          }
        }}
        size={'xl'}
      >
        <Dialog.Header title={t('title', { title: webhookTitle })} />
        <Dialog.Body>
          <Table
            variant={'embedded'}
            loading={loading}
            items={deliveries}
            trackBy={'Id'}
            columnDefinitions={columnDefinitions}
            selectionType={'single'}
            selectedItems={selectedDeliveries}
            onSelectionChange={({ detail }) =>
              setSelectedDeliveries(detail.selectedItems)
            }
            header={
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant={'neutral'}
                  style={'outline'}
                  size={'sm'}
                  disabled={!canRetrigger}
                  onClick={() => setIsConfirmOpen(true)}
                >
                  {t('retriggerButton')}
                </Button>
              </div>
            }
            empty={
              <div style={{ textAlign: 'center' }}>
                {error ? t('loadError') : t('empty')}
              </div>
            }
          />
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant={'neutral'} style={'outline'} onClick={onDismiss}>
            {t('closeButton')}
          </Button>
        </Dialog.Footer>
      </Dialog>
      {viewer ? (
        <Dialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setViewer(null);
            }
          }}
          size={'lg'}
        >
          <Dialog.Header title={viewer.title} />
          <Dialog.Body>
            <pre
              style={{
                margin: 0,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.8125rem',
              }}
            >
              {viewer.content}
            </pre>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              variant={'neutral'}
              style={'outline'}
              onClick={() => setViewer(null)}
            >
              {t('closeButton')}
            </Button>
          </Dialog.Footer>
        </Dialog>
      ) : null}
      <RetriggerDeliveryModal
        open={isConfirmOpen}
        loading={isRetriggering}
        onConfirm={() => void handleRetrigger()}
        onDismiss={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

export default WebhookDeliveryLogsModal;
